type RouteHandler = (params: Record<string, string>) => Promise<HTMLElement>;

interface Route {
  pattern: string;
  handler: RouteHandler;
}

export class Router {
  private routes: Route[] = [];
  private outlet: HTMLElement | null = null;
  private currentCleanup: (() => void) | null = null;

  constructor(outlet: HTMLElement) {
    this.outlet = outlet;
    window.addEventListener('popstate', () => this.resolve());
  }

  add(pattern: string, handler: RouteHandler): void {
    this.routes.push({ pattern, handler });
  }

  navigate(path: string): void {
    history.pushState(null, '', path);
    this.resolve();
  }

  replace(path: string): void {
    history.replaceState(null, '', path);
    this.resolve();
  }

  async resolve(): Promise<void> {
    const path = window.location.pathname;

    // Run cleanup from previous page
    if (this.currentCleanup) {
      this.currentCleanup();
      this.currentCleanup = null;
    }

    for (const route of this.routes) {
      const params = this.matchRoute(route.pattern, path);
      if (params !== null) {
        if (!this.outlet) return;
        this.outlet.innerHTML = '';
        this.outlet.classList.add('loading');
        
        try {
          const element = await route.handler(params);
          this.outlet.innerHTML = '';
          this.outlet.appendChild(element);
          
          // Update document title if data-title attribute is set
          const title = element.getAttribute('data-title');
          if (title) document.title = `${title} — СНТ`;
        } catch (err) {
          console.error('Route error:', err);
          this.outlet.innerHTML = '<div class="error-page"><h2>Ошибка загрузки</h2><p>Попробуйте обновить страницу</p></div>';
        } finally {
          this.outlet.classList.remove('loading');
        }
        return;
      }
    }

    // 404
    if (this.outlet) {
      this.outlet.innerHTML = '<div class="error-page"><h2>404</h2><p>Страница не найдена</p></div>';
    }
  }

  private matchRoute(pattern: string, path: string): Record<string, string> | null {
    const patternParts = pattern.split('/');
    const pathParts = path.split('/');

    if (patternParts.length !== pathParts.length) return null;

    const params: Record<string, string> = {};

    for (let i = 0; i < patternParts.length; i++) {
      const pp = patternParts[i]!;
      const ppp = pathParts[i]!;

      if (pp.startsWith(':')) {
        params[pp.slice(1)] = ppp;
      } else if (pp !== ppp) {
        return null;
      }
    }

    return params;
  }
}

let routerInstance: Router | null = null;

export function createRouter(outlet: HTMLElement): Router {
  routerInstance = new Router(outlet);
  return routerInstance;
}

export function getRouter(): Router {
  if (!routerInstance) throw new Error('Router not initialized');
  return routerInstance;
}
